import {
    PG_CART_SESSION_UUID,
    PG_CART_STATE_UUID,
    RISKIFIED_SESSION_ID,
    WL_CART_SESSION_UUID,
    WL_CART_STATE_UUID,
} from '@/config/cookieNames'
import { xhrApiClient } from '@/config/xhrApiClient'
import StyledErrorMessage from '@/src/common/components/elements/Alerts/StyledErrorMessage'
import { CheckboxInputWithLabel } from '@/src/common/components/elements/Forms/CheckboxInputWithLabel'
import { clearFormErrorForField } from '@/src/common/utilities/clearFormErrorForField'
import getErrorText from '@/src/common/utilities/getErrorText'
import {
    addPaymentInfoAnalyticEvent,
    addPaymentStepAnalyticEvent,
    basicGtagEvent,
    sendBasicEvent,
} from '@/src/common/utilities/gtag'
import { useUser } from '@/src/modules/auth/context/AuthProvider'
import usePostStripePayment from '@/src/common/hooks/usePostStripePayment'
import {
    CheckboxWrapper,
    StyledForm,
} from '@/src/modules/checkout/forms/StyledComponents'
import {
    LinkAuthenticationElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { ValidationError } from 'yup'
import Button from '../../../common/components/marketing/buttons/Button'
import usePostSubmitOrder from '../../../common/hooks/usePostSubmitOrder'
import usePostPurchaseLimitsCheck from '@/src/common/hooks/usePostPurchaseLimitsCheck'
import { FormErrorDisplay } from '../../build-a-card/FormErrorDisplay'
import {
    usePaymentState,
    useUpdatePaymentState,
} from '../CheckoutPaymentContext'
import FiservPaymentFields from './FiservPaymentFields'
import StripePaymentFields from './StripePaymentFields'
import { useCartContext } from '@/src/common/contexts/CartContext'
import { setUserVars } from '@fullstory/browser'
import { TOrderLimitsCodes, TOrderLimitsCodeType } from '../../kyc/types'
import { LOGIN_PAGE } from '../../account/received/constants'
import OverLimitsAlertCheckout from '@/src/common/components/elements/Alerts/OverLimitsAlertCheckout'
import { verifyValidPhoneNumber } from '@/src/common/utilities/verifyValidPhoneNumber'
import buildGoogleEcommerceItemsFromCart from '@/src/common/utilities/analytics/buildGoogleEcommerceItemsFromCart'
import { ICart } from '@/src/modules/cart/ICart'
import PhoneVerificationForm from './PhoneVerificationForm'
import usePhoneVerificationCreate from '@/src/common/hooks/usePhoneVerificationCreate'
import usePhoneVerificationVerify from '@/src/common/hooks/usePhoneVerificationVerify'
import {
    PayPalButtons,
    PayPalButtonsComponentProps,
} from '@paypal/react-paypal-js'
import postCreatePaypalOrder from '@/src/common/queries/PostCreatePaypalOrder'
import { SubmitPaypalOrderInput } from '@/dto/submitPaypalOrderInput'
import postSubmitPaypalOrder from '@/src/common/queries/PostSubmitPaypalOrder'
import PhoneNumberInput from '@/src/common/components/elements/Inputs/PhoneNumberInput'
import { singleRecipientPhoneSchema } from '@/src/modules/build-a-card/validations/schemas'
import limitByPhoneNumberAttempts from '@/src/modules/checkout/utilities/limitByPhoneNumberAttempts'
import HorizontalDivider from '@/src/modules/checkout/horizontalDivider'
import { TStripeFields } from '@/src/modules/checkout/utilities/types'
import TermsAndConditions from '@/src/modules/checkout/content/TermsAndConditions'
import createCheckoutValidationSchema from '@/src/modules/checkout/utilities/createCheckoutValidationSchema'
import AlertWithIcon from '@/src/common/components/elements/Alerts/AlertWithIcon'
import { phone as formatPhone } from 'phone'
import determineCheckoutErrors from '@/src/modules/checkout/utilities/determineCheckoutErrors'
import { PARTNER_SLUG, PHONE_DENIED } from '@/src/common/constants'
import { sendPhoneDeniedFSEvent } from '@/src/common/utilities/fullstory/events'
import handleStripeError from '@/src/modules/checkout/utilities/handleStripeError'
import { PARTNER_CONFIRMATION_PAGE } from '@/src/modules/pg-plus/constants'
import { useCookies } from 'react-cookie'
import { TPartnerPayload } from '@/src/modules/partners/types'

const contactCorporateTeamLink = () => {
    return (
        <>
            Please contact our&nbsp;
            <a rel="noreferrer" target="_blank" href={'/corporate/get-started'}>
                PerfectGift.com Corporate team
            </a>{' '}
            for orders exceeding this limit for a response in minutes!
        </>
    )
}

const invalidFieldsError: string = 'Please complete all form fields'
const invalidEntryError: string =
    'Invalid form data. Please verify the information you provided above.'
const amazonPaymentDeclined: string =
    'Your Amazon payment was declined, please try a different payment method.'

type TLimitItem = {
    code: TOrderLimitsCodes
    type: string
    overAmount: number
}

type TCheckoutFormProps = {
    cartSessionUuid: string
    amazonPayURL: string
    cartData: ICart
    isPayPalEnabled: boolean
    partnerData?: TPartnerPayload
}

export default function CheckoutForm({
    cartSessionUuid,
    cartData,
    amazonPayURL,
    isPayPalEnabled,
    partnerData,
}: Readonly<TCheckoutFormProps>) {
    const { isLoggedIn, user } = useUser()
    const updateState = useUpdatePaymentState()

    const router = useRouter()
    const stripe = useStripe()
    const elements = useElements()

    const { mutateAsync: submitOrder } = usePostSubmitOrder()
    const { mutateAsync: purchaseLimitsCheck } = usePostPurchaseLimitsCheck()
    const { mutateAsync: postPaymentIntent } = usePostStripePayment()
    const { mutate: postPhoneVerificationCreate } = usePhoneVerificationCreate()

    const {
        mutate: postPhoneVerificationResend,
        isSuccess: isSuccessPhoneVerificationResend,
    } = usePhoneVerificationCreate()

    const {
        mutate: postPhoneVerificationVerify,
        isSuccess: isSuccessPhoneVerificationVerify,
        isError: isErrorPhoneVerificationVerify,
        reset: resetPhoneVerification,
    } = usePhoneVerificationVerify()

    const {
        loading,
        paymentProcessor,
        paymentIntent,
        kountSessionId,
        orderTotal,
        totalLoadValue,
        numberOfCards,
        paymentMessage,
        hasPGPlusItem,
        isKYCAndNotRejected,
        virtualLineItemEmails,
        requiresCustomerUpdate,
        requiresSmsVerification,
    } = usePaymentState()

    const {
        setCartTotalLoad,
        setCartTotalCards,
        cartTotalCards,
        cartTotalLoad,
        setOverLimitRequiresKYC,
        setShowKYCForm,
    } = useCartContext()

    const [email, setEmail] = useState<string>('')
    const emailRef = useRef<string>()
    emailRef.current = email

    const [phone, setPhone] = useState<string>('')
    const phoneRef = useRef<string>()
    phoneRef.current = phone

    const [billingName, setBillingName] = useState<string>()
    const [billingAddress, setBillingAddress] = useState<{}>()
    const [agreedToTerms, setAgreedToTerms] = useState<boolean>(true)
    const [displayLiveErrors, setDisplayLiveErrors] = useState<boolean>(false)

    const [showPhoneVerificationModal, setShowPhoneVerificationModal] =
        useState<boolean>(false)
    const [isInStripeFlow, setIsInStripeFlow] = useState<boolean>(false)

    // check purchase limits -> kyc and consumer
    const [overLimitAmount, setOverLimitAmount] = useState<number>(0)
    const [overLimitType, setOverLimitType] = useState<string>()
    const [overLimitCode, setOverLimitCode] = useState<TOrderLimitsCodes>()

    const KYCIsOverWeeklyLimit: boolean = overLimitCode
        ? [
              TOrderLimitsCodes.ORDER_BREAKS_KYC_WEEK_LIMIT,
              TOrderLimitsCodes.KYC_OVER_WEEK_LIMIT,
          ].includes(overLimitCode)
        : false
    const consumerOrderIsOverWeeklyLimit: boolean = overLimitCode
        ? [
              TOrderLimitsCodes.ORDER_BREAKS_WEEK_LIMIT,
              TOrderLimitsCodes.OVER_WEEK_LIMIT,
          ].includes(overLimitCode)
        : false

    const [fieldStatus, setFieldStatus] = useState<TStripeFields>({
        address: false,
        payment: false,
        email: !!email,
        agreedToTerms: agreedToTerms,
    })
    const fieldsAreComplete: boolean = Object.values(fieldStatus).every(
        (field) => field
    )

    const includesFailedAmazonAttempt: boolean = !!router?.query?.amazon_failure

    // standardizing most errors (except checkout error) into one single state -> errorMessage
    const [checkoutFormErrors, setCheckoutFormErrors] = useState<
        ValidationError[]
    >([])

    const maxLoadError: string | null = getErrorText(
        checkoutFormErrors,
        'totalLoadValue'
    )
    const maxCardsError: string | null = getErrorText(
        checkoutFormErrors,
        'numberOfCards'
    )
    const maxCardsOrLoadError: string | null = maxLoadError ?? maxCardsError
    const [errorMessage, setErrorMessage] = useState<string | null>()

    const [phoneError, setPhoneError] = useState<string | null>()
    const hasPhoneErrorRef = useRef<boolean>()
    hasPhoneErrorRef.current = !!phoneError

    // white label partner values
    // only allow self-gift in white label flow
    const isInSelfGiftingPartnerFlow: boolean =
        !!partnerData && !partnerData?.store_page_brand_slug

    const whiteLabelProgramConfig = partnerData?.programConfig
    const whiteLabelCustomPaymentText =
        whiteLabelProgramConfig?.custom_payment_text

    const [cookies, setCookie, removeCookie] = useCookies([
        PG_CART_SESSION_UUID,
        PG_CART_STATE_UUID,
        RISKIFIED_SESSION_ID,
        WL_CART_SESSION_UUID,
        WL_CART_STATE_UUID,
        PARTNER_SLUG,
        RISKIFIED_SESSION_ID,
    ])

    // this controls whether the live errors show (to resolve email validation issue)
    // they'll only show AFTER the button is clicked
    // but will update on any change of email, agreedToTerms, etc.
    useEffect(() => {
        if (!displayLiveErrors) {
            return
        }

        createCheckoutValidationSchema(
            hasPGPlusItem,
            isKYCAndNotRejected,
            virtualLineItemEmails,
            isInSelfGiftingPartnerFlow
        )
            .validate(
                {
                    email,
                    recipientPhone: phone,
                    agreedToTerms,
                    totalLoadValue,
                    numberOfCards,
                },
                { abortEarly: false }
            )
            .then(() => {
                setCheckoutFormErrors([])
            })
            .catch((err: ValidationError) => {
                determineCheckoutErrors(!!email, err, setCheckoutFormErrors)
            })
    }, [
        email,
        phone,
        agreedToTerms,
        totalLoadValue,
        numberOfCards,
        displayLiveErrors,
        hasPGPlusItem,
        isKYCAndNotRejected,
        virtualLineItemEmails,
    ])

    useEffect(() => {
        // remove invalid fields error if form is complete
        if (
            paymentMessage === invalidFieldsError &&
            fieldsAreComplete &&
            !phoneError
        ) {
            setErrorMessage('')
        } else {
            // show max (cards or load) error first
            const errorMessage: string = maxCardsOrLoadError
                ? maxCardsOrLoadError
                : paymentMessage

            setErrorMessage(errorMessage)
        }
    }, [fieldsAreComplete, maxCardsOrLoadError, paymentMessage, phoneError])

    const isSuccessPhoneVerificationVerifyRef = useRef<boolean>()
    isSuccessPhoneVerificationVerifyRef.current =
        isSuccessPhoneVerificationVerify

    // if the shipping form is completed or not required
    // push analytic to identifies continue to checkout (step 2)
    useEffect(() => {
        const formattedCartData = buildGoogleEcommerceItemsFromCart(cartData)
        addPaymentStepAnalyticEvent(formattedCartData)
    }, [cartData])

    const resetCart = () => {
        // Removing the cart cookies effectively clears the cart within the client.
        removeCookie(
            !!partnerData ? WL_CART_SESSION_UUID : PG_CART_SESSION_UUID,
            {
                path: '/',
                domain: window.location.hostname,
            }
        )
        removeCookie(!!partnerData ? WL_CART_STATE_UUID : PG_CART_STATE_UUID, {
            path: '/',
            domain: window.location.hostname,
        })
    }

    const redirectToConfirmationPage = (
        order_number: any,
        email: any,
        is_guest: any,
        hasPartner: boolean
    ) => {
        const path = hasPartner
            ? PARTNER_CONFIRMATION_PAGE
            : '/checkout/confirmation'

        router
            .push({
                pathname: path,
                query: { o: order_number, e: email, g: is_guest },
            })
            .then(() => {
                resetCart()
                setCartTotalCards(0)
                setCartTotalLoad(0)
            })
    }

    const getOrderPayload = () => {
        return {
            email: email,
            cart_session_uuid: cartSessionUuid,
            payment_intent_id: paymentIntent?.id,
            kount_session_id: kountSessionId,
            riskified_session_id:
                window.RISKX?.getCartId() ??
                cookies[RISKIFIED_SESSION_ID] ??
                'unknown',
        }
    }

    const handleOverLimitsReturns = (limit_results: TLimitItem[]) => {
        if (limit_results?.length < 1) {
            return
        }

        // first -> check if the user is over the kyc weekly limit
        let item = limit_results.find(
            (item) => item.code === TOrderLimitsCodes.KYC_OVER_WEEK_LIMIT
        )
        if (item) {
            setOverLimitCode(item.code)
            setOverLimitAmount(item.overAmount)
            return
        }

        // next -> check if they're over the consumer limit
        item = limit_results.find(
            (item) => item.code === TOrderLimitsCodes.OVER_WEEK_LIMIT
        )
        if (item) {
            setOverLimitCode(item.code)
            setOverLimitType(item.type)
            return
        }

        // after that -> check if the new order amount goes over the consumer weekly limit
        item = limit_results.find(
            (item) => item.code === TOrderLimitsCodes.ORDER_BREAKS_WEEK_LIMIT
        )
        if (item) {
            setOverLimitCode(item.code)
            setOverLimitAmount(item.overAmount)
            setOverLimitType(item.type)
            return
        }

        // lastly -> check if the new order amount goes over the KYC weekly limit
        item = limit_results.find(
            (item) =>
                item.code === TOrderLimitsCodes.ORDER_BREAKS_KYC_WEEK_LIMIT
        )
        if (item) {
            setOverLimitCode(item.code)
            setOverLimitAmount(item.overAmount)
            setOverLimitType(item.type)
            return
        }
    }

    const checkLimits = async () => {
        setOverLimitRequiresKYC(false)
        const { limit_results } = await purchaseLimitsCheck({
            order_total: cartTotalLoad,
            card_count: cartTotalCards,
            buyer_email: email,
            token: cartSessionUuid,
        })
        if (limit_results && limit_results.length > 0) {
            handleOverLimitsReturns(limit_results)
            setOverLimitRequiresKYC(true)
            updateState({ loading: false })
            return
        }
    }

    // Grabbing addressElement so we can get a name value for
    //  creating customers in stripe-intent
    const addressElement = elements?.getElement('address')
    addressElement &&
        addressElement.on('change', function (event) {
            if (event.complete) {
                // extract potentially complete address or name
                setBillingAddress(event.value.address)
                setBillingName(event.value.name)

                if (event.value.phone) {
                    setPhone(event.value.phone)
                }
            }
        })

    // Using this to check new limits when items are removed from the cart
    // from right side of checkout page.  cartContext keeps track of cartTotalCards
    // and cartTotalLoad so those signal to recheck.
    useEffect(() => {
        checkLimits().then()
    }, [cartTotalCards, cartTotalLoad])

    const processStripePayment = async () => {
        if (!stripe || !elements) {
            updateState({ loading: false })
            return
        }

        // abort order-submission if stripe elements are invalid
        if ((await elements.submit()).error) {
            updateState({
                loading: false,
                paymentMessage: invalidFieldsError,
            })
            return
        }

        setOverLimitRequiresKYC(false)
        const { limit_results } = await purchaseLimitsCheck({
            // if page is refreshed, cartTotalLoad gets set back to 0.  Only want
            //  to use it if it has been changed during this page session since
            //  totalLoadValue and numberOfCards doesn't update when cart is changed.
            order_total: cartTotalLoad === 0 ? totalLoadValue : cartTotalLoad,
            card_count: cartTotalCards === 0 ? numberOfCards : cartTotalCards,
            buyer_email: email,
            token: cartSessionUuid,
        })
        if (limit_results && limit_results.length > 0) {
            handleOverLimitsReturns(limit_results)
            updateState({ loading: false })
            return
        }

        // only run twilio lookup if phone number has been provided
        const { cleanedPhoneNumber, hasError } =
            phone?.length > 0
                ? await verifyValidPhoneNumber(phone, setPhoneError, true)
                : { cleanedPhoneNumber: '', hasError: false }

        if (hasError) {
            updateState({ loading: false })
            sendBasicEvent(PHONE_DENIED)
            sendPhoneDeniedFSEvent(user, phone)
            return
        }

        if (paymentIntent?.status === 'requires_capture') {
            await submitOrder({
                ...getOrderPayload(),
                phone: cleanedPhoneNumber,
            })
                .then((data) => {
                    const { order_number, email: order_email, is_guest } = data

                    sendBasicEvent(
                        phone ? 'phone_number_added' : 'no_phone_number'
                    )
                    addPaymentInfoAnalyticEvent(orderTotal)
                    redirectToConfirmationPage(
                        order_number,
                        order_email,
                        is_guest,
                        !!partnerData
                    )
                })
                .catch(() => updateState({ loading: false }))
            return
        }

        // add call to our backend to handle get/create customer and update payment intent with that customer id.
        await postPaymentIntent(
            {
                cart_session_uuid: cartSessionUuid,
                payment_intent_id: paymentIntent?.id,
                email_address: email,
                billing_name: billingName,
                billing_address: billingAddress,
                phone_number: phone,
                requires_customer_update: requiresCustomerUpdate,
                statement_descriptor_suffix: cookies.PARTNER_SLUG
                    ? cookies.PARTNER_SLUG
                    : undefined,
            },
            {
                onSuccess: (data) => {
                    updateState({
                        paymentIntent: data.stripe_payment_intent,
                    })
                },
                onError: () => {
                    alert(
                        'Unable to handle payment. Please try again or click Chat Now to get assistance from our GiftGuide.'
                    )
                },
            }
        )

        const { error } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
            confirmParams: {
                return_url: `${amazonPayURL}?kount_session_id=${encodeURIComponent(
                    kountSessionId
                )}&cart_session_uuid=${cartSessionUuid}&`,
                payment_method_data: {
                    billing_details: {
                        email: email,
                        address: {
                            country: 'US',
                        },
                        phone: phone,
                    },
                },
            },
        })

        if (error) {
            updateState({ loading: false })
            handleStripeError(error, updateState, user)

            await xhrApiClient.post(`/api/payment-decline`, {
                error,
                cartId: cartSessionUuid,
                paymentIntentId: paymentIntent?.id,
                email: email,
            })

            return
        }

        await submitOrder({ ...getOrderPayload(), phone: cleanedPhoneNumber })
            .then((data) => {
                const { order_number, email: order_email, is_guest } = data

                sendBasicEvent(phone ? 'phone_number_added' : 'no_phone_number')
                addPaymentInfoAnalyticEvent(orderTotal)
                redirectToConfirmationPage(
                    order_number,
                    order_email,
                    is_guest,
                    !!partnerData
                )
            })
            .catch(() => updateState({ loading: false }))
    }

    const processFiservPayment = async () => {}

    const handleLimitsForceLogin = async (e: any) => {
        e.preventDefault()
        updateState({ loading: true, paymentMessage: '' })
        if (!isLoggedIn) {
            return router
                .replace(`${LOGIN_PAGE}/?type=checkout&overLimits=true`)
                .then()
        }
        setShowKYCForm(true)
    }

    const handleSubmit = async (e: any) => {
        e?.preventDefault()

        setIsInStripeFlow(true)
        setDisplayLiveErrors(true)

        updateState({ loading: true, paymentMessage: '' })

        let validateIsSuccess: boolean = true
        await createCheckoutValidationSchema(
            hasPGPlusItem,
            isKYCAndNotRejected,
            virtualLineItemEmails,
            isInSelfGiftingPartnerFlow
        )
            .validate(
                {
                    email,
                    recipientPhone: phone,
                    agreedToTerms,
                    totalLoadValue,
                    numberOfCards,
                },
                { abortEarly: false }
            )
            .then(() => {
                setCheckoutFormErrors([])
            })
            .catch((err: ValidationError) => {
                determineCheckoutErrors(!!email, err, setCheckoutFormErrors)
                validateIsSuccess = false
            })

        try {
            setUserVars({
                uid: cartSessionUuid,
            })
        } catch (e) {
            console.debug(e)
        }

        // if they have OR their validation failed, block them from continuing checkout
        if (phoneError || !validateIsSuccess) {
            // if something is wrong with form data, show error
            setErrorMessage(invalidEntryError)
            setIsInStripeFlow(false)
            updateState({ loading: false })
            return
        }

        // Show phone verification modal if not completed
        if (!isSuccessPhoneVerificationVerify && requiresSmsVerification) {
            console.log('phone not successful yet, create phone verification')
            return await handleCreatePhoneVerification(phone)
        }

        return paymentProcessor === 'stripe'
            ? await processStripePayment()
            : await processFiservPayment()
    }

    useEffect(() => {
        // For edge-case where SMS is completed AFTER payment is filled out.
        // Check if stripe credit card form was filled out and auto-submit payment.
        if (
            isSuccessPhoneVerificationVerify &&
            Object.keys(billingAddress ?? {}).length > 0 &&
            isInStripeFlow
        ) {
            updateState({
                loading: true,
            })
            processStripePayment().then()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccessPhoneVerificationVerify])

    const handleCreatePhoneVerification = async (phone: string) => {
        const formattedPhone = formatPhone(phone)
        postPhoneVerificationCreate(
            {
                phone_number: formattedPhone.phoneNumber,
                cart_uuid: cartSessionUuid,
            },
            {
                onSuccess: () => {
                    setShowPhoneVerificationModal(true)
                    basicGtagEvent('phone_verification_started')
                },
                onError: () => {
                    updateState({
                        loading: false,
                        paymentMessage: `Error verifying your phone number. Please check your information and try again.`,
                    })
                },
            }
        )
    }

    const handleSubmitPhoneVerificationCode = async (code: string) => {
        const formattedPhone = formatPhone(phone)
        postPhoneVerificationVerify(
            {
                phone_number: formattedPhone.phoneNumber,
                code: code,
                cart_uuid: cartSessionUuid,
            },
            {
                onSuccess: () => {
                    setTimeout(() => {
                        setShowPhoneVerificationModal(false)
                    }, 1000)
                    basicGtagEvent('phone_verification_completed')
                },
            }
        )
    }

    useEffect(() => {
        if (!showPhoneVerificationModal && !isSuccessPhoneVerificationVerify) {
            // re-enable the checkout form if phone verification modal is closed and we are not verified
            updateState({ loading: false, paymentMessage: '' })
        }
    }, [
        showPhoneVerificationModal,
        isSuccessPhoneVerificationVerify,
        updateState,
    ])

    // PAYPAL:
    let hasValidationErrors: boolean
    const createOrder: PayPalButtonsComponentProps['createOrder'] =
        async () => {
            console.log('paypal-createOrder init')

            setDisplayLiveErrors(true)
            updateState({ loading: true })
            const currentPhone = phoneRef.current ?? ''
            const purchaseEmail = emailRef.current ?? ''
            const hasPhoneError = hasPhoneErrorRef.current ?? false

            // formatted phone used to check attempts and phone verification
            const { phoneNumber: formattedPhone } = formatPhone(currentPhone)

            const checkoutValidationSchema = createCheckoutValidationSchema(
                hasPGPlusItem,
                isKYCAndNotRejected,
                virtualLineItemEmails,
                isInSelfGiftingPartnerFlow
            )

            await checkoutValidationSchema
                .validate(
                    {
                        recipientPhone: currentPhone,
                        email: purchaseEmail,
                        agreedToTerms,
                        totalLoadValue,
                        numberOfCards,
                    },
                    {
                        abortEarly: false,
                    }
                )
                .then(() => {
                    console.log('paypal-createOrder validation success')
                    hasValidationErrors = false
                    setCheckoutFormErrors([])
                })
                .catch((err) => {
                    console.log(
                        'paypal-createOrder validation error',
                        err.inner
                    )
                    determineCheckoutErrors(
                        !!purchaseEmail,
                        err,
                        setCheckoutFormErrors
                    )
                    hasValidationErrors = true
                })

            // if they've attempted phone too many times OR their validation failed,
            // block them from continuing checkout
            if (hasValidationErrors || hasPhoneError) {
                if (hasValidationErrors) {
                    setErrorMessage(invalidEntryError)
                }

                updateState({ loading: false })
                return
            }

            // Show phone verification modal if not completed
            if (
                !isSuccessPhoneVerificationVerifyRef.current &&
                currentPhone.length > 0 &&
                requiresSmsVerification
            ) {
                console.log('paypal-createOrder create phone verification')

                if (formattedPhone) {
                    await handleCreatePhoneVerification(formattedPhone)

                    updateState({ loading: false })
                }

                return
            }

            try {
                let response = await postCreatePaypalOrder({
                    cart_session_uuid: cartSessionUuid,
                    phone_number: currentPhone,
                })

                console.log('paypal-createOrder postCreatePaypalOrder success')

                return response.id
            } catch (error: any) {
                console.error(
                    'paypal-createOrder failed to create order in API',
                    error
                )
            }
        }

    const onPaypalApprove: PayPalButtonsComponentProps['onApprove'] = async (
        data,
        actions
    ) => {
        actions.order?.authorize().then((auth) => {
            updateState({ loading: true })

            if (!auth.purchase_units?.[0]?.payments?.authorizations?.[0]) {
                updateState({ loading: false })
                return
            }

            const authID =
                auth.purchase_units[0].payments.authorizations[0].id ?? ''

            const currentPhone = phoneRef.current ?? ''
            const formattedPhone = String(currentPhone).replace(/\D/g, '')

            if (authID === '') {
                console.error('Paypal Order Authorization Error', auth)
            }

            // @ts-ignore
            postSubmitPaypalOrder({
                cart_session_uuid: cartSessionUuid,
                auth_id: authID,
                billing_token: data.billingToken,
                facilitator_access_token: data.facilitatorAccessToken,
                paypal_order_id: data.paymentID,
                paypal_payer_id: data.payerID,
                payment_id: data.paymentID,
                phone_number: formattedPhone,
                // @ts-ignore
                payment_source: data.paymentSource ?? '',
                // @ts-ignore
                paypal_email: auth.payer?.email_address ?? '',
                status: auth.status,
                buyer_email: emailRef.current ?? '',
                kount_session_id: kountSessionId ?? 'kount_session_id',
                riskified_session_id:
                    window.RISKX?.getCartId() ??
                    cookies[RISKIFIED_SESSION_ID] ??
                    'unknown',
            } as SubmitPaypalOrderInput).then((data) => {
                const { order_number, email: order_email, is_guest } = data

                sendBasicEvent(phone ? 'phone_number_added' : 'no_phone_number')
                addPaymentInfoAnalyticEvent(orderTotal)
                redirectToConfirmationPage(
                    order_number,
                    order_email,
                    is_guest,
                    !!partnerData
                )
            })
        })
    }

    const validatePhone = (phoneNumber: string) => {
        const validation = {
            recipientPhone: phoneNumber,
        }

        return singleRecipientPhoneSchema.validate(validation, {
            abortEarly: false,
            context: {
                checkPhoneBlocked: true,
            },
        })
    }

    return (
        <>
            {includesFailedAmazonAttempt && (
                <AlertWithIcon
                    iconName={'error'}
                    size={'sm'}
                    style={{ marginTop: 15, width: 450, maxWidth: '100%' }}
                >
                    <p>{amazonPaymentDeclined}</p>
                </AlertWithIcon>
            )}
            <PhoneVerificationForm
                submitPhoneVerificationCode={handleSubmitPhoneVerificationCode}
                showModal={showPhoneVerificationModal}
                setShowModal={setShowPhoneVerificationModal}
                phone={phone}
                success={isSuccessPhoneVerificationVerify}
                resendPhoneVerificationCode={async () => {
                    const formattedPhone = formatPhone(phone)
                    postPhoneVerificationResend({
                        phone_number: formattedPhone.phoneNumber,
                        cart_uuid: cartSessionUuid,
                    })
                }}
                resendSuccess={isSuccessPhoneVerificationResend}
                verificationError={isErrorPhoneVerificationVerify}
            />

            <StyledForm
                id="payment-form"
                onSubmit={
                    consumerOrderIsOverWeeklyLimit
                        ? handleLimitsForceLogin
                        : handleSubmit
                }
            >
                <p className={'form-header'}>Contact information</p>

                <LinkAuthenticationElement
                    onChange={(event) => {
                        const email = event.value.email
                        setEmail(email)

                        // determine if stripe field is complete
                        setFieldStatus((prev) => {
                            return { ...prev, email: event.complete }
                        })

                        // if email is filled out, set FS var
                        if (event.complete) {
                            try {
                                setUserVars({
                                    email: email,
                                })
                            } catch (e) {
                                console.debug(e)
                            }

                            // if cart has existing stripe customer or has failed payment
                            // set update customer to true
                            if (
                                !!cartData?.stripe_payment_intent?.customer ||
                                isInStripeFlow
                            ) {
                                updateState({ requiresCustomerUpdate: true })
                            }
                        }
                    }}
                    options={{
                        defaultValues: {
                            email: user?.email ?? '',
                        },
                    }}
                />
                {/*do not show valid email error because Stripe has one*/}
                <FormErrorDisplay
                    formErrors={checkoutFormErrors}
                    path={'email'}
                    className={'error stripe'}
                    style={{ marginBottom: 0 }}
                />
                <br />
                <PhoneNumberInput
                    inputStyle={'stripe'}
                    phone={phone}
                    setPhone={async (newPhone: string) => {
                        setPhoneError(null)
                        setIsInStripeFlow(false)
                        // if new phone number entered, reset verification
                        if (phone !== newPhone) {
                            resetPhoneVerification()
                        }

                        setPhone(newPhone)
                        validatePhone(newPhone)
                            .then(async () => {
                                const { phoneNumber: formattedPhone } =
                                    formatPhone(newPhone)

                                // set FullStory var
                                try {
                                    setUserVars({
                                        phone: formattedPhone,
                                    })
                                } catch (e) {
                                    console.debug(e)
                                }

                                // check if user has tried too many phone numbers
                                const { continueCheckout } =
                                    await limitByPhoneNumberAttempts({
                                        phone: String(formattedPhone),
                                        setPhoneError,
                                        user,
                                    })

                                // if they haven't, show them the verification modal
                                if (
                                    !isSuccessPhoneVerificationVerify &&
                                    requiresSmsVerification &&
                                    continueCheckout &&
                                    formattedPhone
                                ) {
                                    await handleCreatePhoneVerification(
                                        formattedPhone
                                    )
                                }
                            })
                            .catch((err: any) => {
                                const otherErrors =
                                    checkoutFormErrors?.filter(
                                        (error) =>
                                            error.path !== 'recipientPhone'
                                    ) ?? []

                                setCheckoutFormErrors([
                                    ...otherErrors,
                                    ...err.inner,
                                ])
                            })
                    }}
                    formErrors={checkoutFormErrors}
                    setFormErrors={setCheckoutFormErrors}
                    fieldId={'recipientPhone'}
                    placeholder={'Phone number'}
                />
                {phoneError && (
                    <StyledErrorMessage
                        errorText={phoneError}
                        style={{ marginBottom: 5 }}
                    />
                )}

                <p className={'form-header smaller-top-margin'}>
                    Payment information
                </p>
                {whiteLabelCustomPaymentText && (
                    <p
                        style={{
                            fontSize: '.95rem',
                            marginBottom: 20,
                        }}
                    >
                        {whiteLabelCustomPaymentText}
                    </p>
                )}
                {isPayPalEnabled && (
                    <>
                        <PayPalButtons
                            style={{ layout: 'horizontal', tagline: false }}
                            createOrder={createOrder}
                            onApprove={onPaypalApprove}
                            onCancel={() => {
                                updateState({ loading: false })
                            }}
                            onError={() => {
                                updateState({ loading: false })
                            }}
                        />

                        <HorizontalDivider />
                    </>
                )}

                {paymentProcessor === 'fiserv' ? (
                    <FiservPaymentFields />
                ) : (
                    <StripePaymentFields setIsComplete={setFieldStatus} />
                )}

                <CheckboxWrapper>
                    <CheckboxInputWithLabel
                        id={'agreedToTerms'}
                        data-testid={'agreedToTerms'}
                        name={'agreedToTerms'}
                        value={agreedToTerms}
                        label={TermsAndConditions()}
                        checked={agreedToTerms}
                        onCheckedChange={(checked: boolean) => {
                            setAgreedToTerms(checked)
                            clearFormErrorForField({
                                path: 'agreedToTerms',
                                formErrors: checkoutFormErrors,
                                setFormErrors: setCheckoutFormErrors,
                            })
                        }}
                    />
                    <FormErrorDisplay
                        formErrors={checkoutFormErrors}
                        path={'agreedToTerms'}
                    />
                </CheckboxWrapper>

                {errorMessage && (
                    <StyledErrorMessage
                        errorText={
                            <>
                                {errorMessage}{' '}
                                {maxCardsOrLoadError &&
                                    contactCorporateTeamLink()}
                            </>
                        }
                        style={{ marginBottom: 5 }}
                    />
                )}

                {overLimitType && (
                    <OverLimitsAlertCheckout
                        overLimitCodeType={overLimitCode}
                        overLimitAmount={overLimitAmount}
                        overLimitType={overLimitType as TOrderLimitsCodeType}
                    />
                )}

                {/*if consumer is over weekly limit, make them provide more info -> login or kyc form*/}
                {consumerOrderIsOverWeeklyLimit ? (
                    <Button
                        css={{
                            margin: '10px 0 15px',
                        }}
                        variant={'primary'}
                        size={'md'}
                        showArrow
                        textTransform={'uppercase'}
                        data-testid={'checkout-button'}
                        label={isLoggedIn ? 'Provide more info' : 'Log In'}
                        isLoading={loading}
                        disabled={loading}
                        onSubmit={handleLimitsForceLogin}
                        id="submit"
                        width="full"
                        type={'submit'}
                    />
                ) : (
                    <Button
                        css={{
                            margin: '10px 0 15px',
                        }}
                        variant={fieldsAreComplete ? 'primary' : 'deactivated'}
                        size={'md'}
                        showArrow
                        textTransform={'uppercase'}
                        data-testid={'checkout-button'}
                        label={'Pay now'}
                        isLoading={loading}
                        disabled={loading || KYCIsOverWeeklyLimit}
                        id="submit"
                        width="full"
                        type={'submit'}
                    />
                )}
            </StyledForm>
        </>
    )
}
